import { LitElement, html } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { live } from 'lit/directives/live.js';
import { Copy, Check, Crown, UserMinus, Trash2, Mail, FolderCog, Info, MoreHorizontal, Activity, X as XIcon, Search, UserPlus } from 'lucide';
import { auth } from '../auth.js';
import { api } from '../api.js';
import { displayName } from '@mv/models';
import { lucideIcon } from '../icon.js';
import { confirmDialog, alertDialog } from '../utils/confirm-dialog.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';
import { formatDateTimeSlash } from '../utils/datetime.js';
import './mv-table.js';
import './activity-tile.js';
import './searchable-select.js';

/** Eén regel in het ⋯-menu van een teamlid; zelfde vorm als het projectmenu. */
const MENU_ITEM = 'display:flex; align-items:center; gap:.65rem; width:100%; padding:.6rem .65rem; border:none; background:none; border-radius:.5rem; cursor:pointer; font:inherit; font-size:.88rem; color:var(--mv-text,#181c32); text-align:left;';

// Gast heeft rang 0, zoals in de backend. Zonder die regel is RANK['guest'] undefined en zegt
// `undefined <= 2` false — dan kon een beheerder een gast niet van de Team-pagina verwijderen,
// terwijl de backend dat wel toestaat.
const RANK = { owner: 3, manager: 2, member: 1, guest: 0 };

// Custom chevron placed close to the text (replaces the native arrow), shared
// with /admin/analysis for a consistent look. Append to a <select> inline style.
const CHEVRON = "url(\"data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='14'%20height='14'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%236c757d'%20stroke-width='2.5'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Cpolyline%20points='6%209%2012%2015%2018%209'/%3E%3C/svg%3E\")";
const CHEVRON_CSS = `-webkit-appearance:none; appearance:none; background-image:${CHEVRON}; background-repeat:no-repeat; background-position:right 10px center; background-size:13px; padding-right:1.9rem;`;
const roleLabel = (r) =>
  r === 'owner' ? 'Eigenaar' : r === 'manager' ? 'Beheerder' : r === 'member' ? 'Lid' : r === 'guest' ? 'Gast' : (r || '—');

/**
 * Team page for the ACTIVE company. Lists members with role controls and a ⋯
 * menu (transfer ownership / remove), plus invitations (invite by e-mail with a
 * copy-link MVP, and a pending-invitations tab). All rules are enforced
 * server-side; the UI mirrors them for affordance.
 *  - members:     GET  /api/team/members  → { members, myRole, myUserId }
 *  - invitations: GET  /api/team/invitations
 *  - invite/role/remove/transfer/revoke → /api/team/*
 */
export class TeamPage extends LitElement {
  // Render in light DOM so the app's global styles apply; new UI uses inline styles.
  createRenderRoot() { return this; }

  static properties = {
    tab: { type: String },
    members: { type: Array },
    invitations: { type: Array },
    myRole: { type: String },
    myUserId: { type: String },
    loading: { type: Boolean },
    error: { type: String },
    busy: { type: Boolean },
    inviteEmail: { type: String },
    inviteFirstName: { type: String },
    inviteLastName: { type: String },
    inviteRole: { type: String },
    // Alleen bij rol 'gast': het project en het niveau. Een gast zonder project heeft nergens
    // toegang toe, dus die twee zijn dan verplicht.
    inviteProjectId: { type: String },
    inviteAccessLevel: { type: String },
    _projects: { type: Array, state: true },
    inviteResult: { type: Object },
    inviteError: { type: String },
    _roleInfoOpen: { type: Boolean, state: true },
    _roleInfoModalOpen: { type: Boolean, state: true },
    copiedToken: { type: String },
    accessUser: { type: Object },
    accessProjects: { type: Array },
    accessLoading: { type: Boolean },
    accessBusy: { type: Boolean },
    // Zoekveld in de toegangspopup: bij tientallen projecten scrol je anders eindeloos.
    _accessQuery: { type: String, state: true },
    // Uitnodigen zit achter een popup: het formulier hoort niet permanent bovenaan te staan
    // als je meestal komt om te kijken wie er in het team zit.
    _inviteOpen: { type: Boolean, state: true },
    // Zoeken op naam of e-mail, en filteren op rol, boven de tabel.
    _search: { type: String, state: true },
    _roleFilter: { type: String, state: true },
    // ⋯-menu per rij (id van het lid waarvan het menu openstaat) en de activiteitpopup.
    _menuFor: { type: String, state: true },
    _menuPos: { type: Object, state: true },
    _activityUser: { type: Object, state: true },
    // Mogen systeembeheerders van GeoVoice meekijken? `Loaded` staat er apart bij: zolang we het
    // niet weten tonen we de tegel niet, want een schakelaar die 'uit' toont terwijl hij aan
    // staat is erger dan even geen schakelaar.
    sysadminAccess: { type: Boolean },
    sysadminAccessLoaded: { type: Boolean },
  };

  constructor() {
    super();
    this.tab = 'members';
    this.members = [];
    this.invitations = [];
    this.myRole = 'member';
    this.myUserId = null;
    this.loading = false;
    this.error = null;
    this.busy = false;
    this.inviteEmail = '';
    this.inviteFirstName = '';
    this.inviteLastName = '';
    this.inviteRole = ''; // force an explicit choice (role is a required field)
    this.inviteProjectId = '';
    this.inviteAccessLevel = 'read';
    this._projects = null;
    this._roleInfoOpen = false;
    this._roleInfoModalOpen = false;
    this.inviteResult = null;
    this.inviteError = null;
    this.copiedToken = null;
    this.accessUser = null;
    this.accessProjects = [];
    this.accessLoading = false;
    this.accessBusy = false;
    this._menuFor = null;
    this._activityUser = null;
    this._inviteOpen = false;
    this._search = '';
    this._roleFilter = '';
    this.sysadminAccess = false;
    this.sysadminAccessLoaded = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.load();
    this._onEsc = (e) => {
      if (e.key !== 'Escape') return;
      if (this.accessUser) this.closeAccess();
      if (this._activityUser) this._activityUser = null;
      if (this._menuFor) this._menuFor = null;
      if (this._inviteOpen) this._inviteOpen = false;
      if (this._roleInfoModalOpen) this._roleInfoModalOpen = false;
    };
    document.addEventListener('keydown', this._onEsc);
    // Klik buiten het ⋯-menu sluit het; de rijen zelf stoppen de klik niet.
    this._onDocClick = () => { if (this._menuFor) this._menuFor = null; };
    document.addEventListener('click', this._onDocClick);
    // Het menu is position:fixed. Scroll je met het menu open, dan schuift de knop weg terwijl
    // het menu op zijn plek in het scherm blijft hangen. Sluiten is eenvoudiger dan meebewegen.
    this._onScroll = () => { if (this._menuFor) this._menuFor = null; };
    document.addEventListener('scroll', this._onScroll, true);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onEsc);
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('scroll', this._onScroll, true);
    super.disconnectedCallback();
  }

  async load() {
    this.loading = true;
    this.error = null;
    try {
      const data = await api.getTeamMembers();
      this.members = data.members || [];
      this.myRole = data.myRole || 'member';
      this.myUserId = data.myUserId || null;
      // De openstaande uitnodigingen zijn voor beheerders; de backend geeft een lid een 403.
      // Die vroegen we toch op, en dan stond er bij elk bezoek een rode foutmelding boven de
      // pagina van iemand die niets fout deed.
      this.invitations = this.canSeeInvitations ? await api.getTeamInvitations() : [];
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
    }

    // De meekijk-schakelaar zit op het bedrijfsprofiel. Los opgehaald en met een eigen vangnet:
    // mislukt dit, dan hoort de ledenlijst er nog gewoon te staan.
    try {
      const profile = await api.getCompanyProfile();
      this.sysadminAccess = !!profile?.sysadminAccess;
      this.sysadminAccessLoaded = true;
    } catch { /* schakelaar blijft uit beeld */ }
  }

  /** Meekijken door systeembeheerders aan- of uitzetten voor dit bedrijf. */
  async saveSysadminAccess(enabled) {
    this.busy = true;
    this.error = null;
    try {
      await api.setCompanySysadminAccess(enabled);
      this.sysadminAccess = enabled;
    } catch (e) {
      this.error = `Opslaan mislukt: ${e.message}`;
    } finally {
      this.busy = false;
    }
  }

  // ── Permission mirrors (authoritative checks are server-side) ──────────
  get canInvite() { return true; }
  /** Zelfde grens als GET /api/team/invitations: beheerder of eigenaar. */
  get canSeeInvitations() { return (RANK[this.myRole] ?? 0) >= RANK.manager; }
  /**
   * Alle uitnodigbare rollen, met per rol of JIJ hem mag toekennen. Wat je niet mag staat er
   * grijs in met de reden, en verdwijnt niet: een lid dat alleen 'Lid' ziet weet niet dát er
   * meer is, laat staan waarom hij het niet mag. De regel zelf staat in de backend; dit is de
   * weergave ervan.
   *
   *   beheerder  → nodigt beheerders, leden en gasten uit
   *   lid        → nodigt leden en gasten uit
   *   gast       → nodigt niemand uit (ziet deze pagina niet eens)
   */
  get roleOptions() {
    const mine = RANK[this.myRole] ?? 0;
    return [
      { value: 'manager', label: 'Beheerder', allowed: mine >= RANK.manager, why: 'Alleen een beheerder nodigt beheerders uit' },
      { value: 'member',  label: 'Lid',       allowed: mine >= RANK.member },
      { value: 'guest',   label: 'Gast',      allowed: mine >= RANK.member },
    ];
  }
  get assignableRoles() {
    // Voor de rol-dropdown bij een bestaand lid; gast staat er niet in — die rol geef je via
    // een uitnodiging op een project, en een lid tot gast maken zou zijn toegang inkrimpen.
    return ['member', 'manager'].filter((r) => RANK[r] <= RANK[this.myRole]);
  }
  canManage(m) {
    return RANK[this.myRole] >= RANK.manager
      && m.role !== 'owner'
      && m.id !== this.myUserId
      && RANK[m.role] <= RANK[this.myRole];
  }
  canTransfer(m) {
    return this.myRole === 'owner' && m.role !== 'owner' && m.id !== this.myUserId;
  }
  /**
   * Een gast kan geen eigenaar worden: hij is een externe met toegang tot losse projecten,
   * geen lid van het bedrijf. De menuregel blijft wel staan, maar grijs — weglaten zou de
   * eigenaar laten zoeken waarom die regel bij de ene rij wel en bij de andere niet staat.
   */
  transferBlocked(m) {
    return m.role === 'guest';
  }

  acceptUrl(token) { return `${window.location.origin}/accept-invite?token=${token}`; }

  async copy(text, token) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      ta.remove();
    }
    this.copiedToken = token;
    setTimeout(() => { if (this.copiedToken === token) { this.copiedToken = null; } }, 1800);
  }

  /**
   * Link kopiëren van een openstaande uitnodiging. Het token zit niet meer in de lijst, dus
   * dat halen we hier op — één aanroep per keer dat iemand er echt om vraagt.
   */
  async copyInviteLink(inv) {
    try {
      const token = await api.getInvitationLink(inv.id);
      if (!token) throw new Error('Geen link ontvangen');
      await this.copy(this.acceptUrl(token), inv.id);
    } catch (err) {
      alertDialog({ message: `Link ophalen mislukt: ${err.message}` });
    }
  }

  async submitInvite(e) {
    e?.preventDefault?.();
    const email = (this.inviteEmail || '').trim();
    const firstName = (this.inviteFirstName || '').trim();
    const lastName = (this.inviteLastName || '').trim();
    const role = this.inviteRole;
    // Voornaam, achternaam, e-mail én rol zijn allemaal verplicht.
    if (!firstName || !lastName) { this.inviteError = 'Voornaam en achternaam zijn verplicht.'; return; }
    if (!email) { this.inviteError = 'E-mailadres is verplicht.'; return; }
    if (!role) { this.inviteError = 'Kies een rol.'; return; }
    const extra = {};
    if (role === 'guest') {
      if (!this.inviteProjectId) { this.inviteError = 'Kies het project waar de gast bij moet.'; return; }
      extra.projectId = this.inviteProjectId;
      extra.accessLevel = this.inviteAccessLevel || 'read';
    }
    this.busy = true;
    this.inviteError = null;
    this.inviteResult = null;
    try {
      const res = await api.inviteTeamMember(email, role, firstName, lastName, extra);
      this.inviteResult = res;
      this.inviteEmail = ''; this.inviteFirstName = ''; this.inviteLastName = ''; this.inviteRole = '';
      this.inviteProjectId = ''; this.inviteAccessLevel = 'read';
      // Popup dicht: de bevestiging (en de kopieerlink) komt onder de werkbalk, naast de
      // nieuwe rij — daar wil je hem zien, niet in een venster dat je nog moet sluiten.
      this._inviteOpen = false;
      await this.load();
      // Een lid ziet de lijst niet; de bevestiging met de link staat ook onder Leden.
      if (res.invited && res.token && this.canSeeInvitations) this.tab = 'invitations';
    } catch (err) {
      this.inviteError = err.message;
    } finally {
      this.busy = false;
    }
  }

  /** Bij 'gast' de projectlijst ophalen, eenmalig; die is pas nodig als iemand die rol kiest. */
  async _onInviteRoleChange(role) {
    this.inviteRole = role;
    if (role === 'guest' && this._projects === null) {
      try { this._projects = await api.getProjects(); } catch { this._projects = []; }
    }
  }

  async changeRole(m, role) {
    if (role === m.role) return;
    this.busy = true;
    try {
      await api.changeMemberRole(m.id, role);
      await this.load();
    } catch (err) {
      alertDialog({ message: `Rol wijzigen mislukt: ${err.message}` });
    } finally {
      this.busy = false;
    }
  }

  async removeMember(m) {
    const ok = await confirmDialog({
      title: 'Lid verwijderen?',
      message: `${m.email} verliest direct toegang tot dit bedrijf en alle bijbehorende projecten. Dit kan niet ongedaan worden gemaakt — wel kun je ze later opnieuw uitnodigen.`,
      confirmLabel: 'Ja, verwijderen',
      danger: true,
    });
    if (!ok) return;
    this.busy = true;
    try {
      await api.removeMember(m.id);
      await this.load();
    } catch (err) {
      alertDialog({ message: `Verwijderen mislukt: ${err.message}` });
    } finally {
      this.busy = false;
    }
  }

  async transfer(m) {
    const ok = await confirmDialog({
      title: 'Eigenaarschap overdragen?',
      message: `Je draagt het eigenaarschap van dit bedrijf over aan ${m.email}. Zij worden de nieuwe eigenaar en jij wordt beheerder. Je kunt dit daarna niet zelf terugdraaien — alleen de nieuwe eigenaar kan het terug overdragen.`,
      confirmLabel: 'Ja, overdragen',
      danger: true,
    });
    if (!ok) return;
    this.busy = true;
    try {
      await api.transferOwnership(m.id);
      await this.load();
      // My own role changed (owner → manager); refresh it for the next token via re-login,
      // but reflect it now from the reloaded members list.
    } catch (err) {
      alertDialog({ message: `Overdragen mislukt: ${err.message}` });
    } finally {
      this.busy = false;
    }
  }

  async revoke(inv) {
    const ok = await confirmDialog({
      title: 'Uitnodiging intrekken?',
      message: `De uitnodiging voor ${inv.email} wordt ingetrokken. De bijbehorende link werkt daarna niet meer.`,
      confirmLabel: 'Ja, intrekken',
      danger: true,
    });
    if (!ok) return;
    this.busy = true;
    try {
      await api.revokeInvitation(inv.id);
      await this.load();
    } catch (err) {
      alertDialog({ message: `Intrekken mislukt: ${err.message}` });
    } finally {
      this.busy = false;
    }
  }

  // ── Per-project access ────────────────────────────────────────────────

  async openAccess(m) {
    this.accessUser = m;
    this.accessProjects = [];
    this._accessQuery = '';
    this.accessLoading = true;
    try {
      const data = await api.getMemberProjectAccess(m.id);
      this.accessProjects = data.projects || [];
    } catch (err) {
      alertDialog({ message: `Projecttoegang laden mislukt: ${err.message}` });
      this.accessUser = null;
    } finally {
      this.accessLoading = false;
    }
  }

  closeAccess() { this.accessUser = null; this.accessProjects = []; this._accessQuery = ''; }

  toggleProjectAccess(projectId) {
    this.accessProjects = this.accessProjects.map(p =>
      p.id === projectId ? { ...p, hasAccess: !p.hasAccess } : p);
  }

  /** Gast: niveau per project — null (geen), 'read' of 'write'. */
  setGuestLevel(projectId, level) {
    this.accessProjects = this.accessProjects.map(p =>
      p.id === projectId ? { ...p, hasAccess: !!level, accessLevel: level } : p);
  }

  async saveAccess() {
    const isGuest = this.accessUser?.role === 'guest';
    const payload = isGuest
      ? { guestAccess: this.accessProjects.filter(p => p.accessLevel).map(p => ({ projectId: p.id, accessLevel: p.accessLevel })) }
      : { deniedProjectIds: this.accessProjects.filter(p => !p.hasAccess).map(p => p.id) };
    this.accessBusy = true;
    try {
      await api.setMemberProjectAccess(this.accessUser.id, payload);
      this.closeAccess();
      // De kolom 'Toegang projecten' toont de nieuwe stand meteen; anders zie je je eigen
      // wijziging pas na een verversing en lijkt het alsof er niets is opgeslagen.
      await this.load();
    } catch (err) {
      alertDialog({ message: `Opslaan mislukt: ${err.message}` });
    } finally {
      this.accessBusy = false;
    }
  }

  // ── Rendering ──────────────────────────────────────────────────────────

  roleBadge(role) {
    const bg = role === 'owner' ? 'rgba(245,158,11,0.15)' : role === 'manager' ? 'rgba(59,130,246,0.12)' : 'var(--mv-bg,#f1f3f5)';
    const fg = role === 'owner' ? '#b45309' : role === 'manager' ? '#1d4ed8' : 'var(--mv-text-sub,#6c757d)';
    return html`<span style="display:inline-flex; align-items:center; gap:5px; padding:3px 10px; border-radius:999px; font-size:0.78rem; font-weight:700; background:${bg}; color:${fg};">
      ${role === 'owner' ? html`<span style="display:flex;">${unsafeSVG(lucideIcon(Crown, 13))}</span>` : ''}${roleLabel(role)}
    </span>`;
  }

  /**
   * Kolom 'Toegang projecten': wie kan waar bij, en waaróm. De kleur vertelt het model:
   *
   *   beheerder  → 'alles'                    heeft altijd toegang, niet te beperken
   *   lid        → 'alles' of RODE tegels     opt-out: alles, behalve de uitgesloten projecten
   *   gast       → 'niets' of GROENE tegels   opt-in: niets, behalve de toegekende projecten
   *
   * Uitleg zit in de hover en niet in de cel: de cel moet scanbaar blijven, en 'waarom rood'
   * hoef je maar één keer te lezen. Elke tegel linkt naar het project — beheren gebeurt dáár,
   * want dat is de plek die het model en (bij gasten) het niveau kent.
   */
  renderProjectAccess(m) {
    const tip = (text, node) => html`<span data-tip=${text} @mousemove=${tipMove} @mouseleave=${tipHide} style="display:inline-flex;">${node}</span>`;
    const word = (text, color) => html`<span style="font-size:0.82rem; font-weight:600; color:${color}; cursor:help;">${text}</span>`;
    // Zelfde vorm als de licentietegel op /admin/companies (.status-pill): geen rand, hoeken
    // van 4px, een zacht vlak met bijpassende tekstkleur. De pagina rendert in de light DOM,
    // dus de klasse uit mv-table bereikt hem niet; de waarden staan hier uitgeschreven.
    const chip = (p, { bg, fg }, tipText) => tip(tipText, html`
      <a href=${`/projects/${p.id}`} @click=${(e) => e.stopPropagation()}
        style="display:inline-flex; align-items:center; padding:0.25rem 0.5rem; border-radius:4px; font-size:0.75rem; font-weight:600; text-decoration:none;
               background:${bg}; color:${fg}; white-space:nowrap; max-width:180px; overflow:hidden; text-overflow:ellipsis;">
        ${p.code || p.name || 'project'}
      </a>`);
    const RED = { bg: 'var(--mv-danger-bg,#fff5f8)', fg: 'var(--mv-danger,#f1416c)' };
    const GREEN = { bg: 'var(--mv-primary-light,#f4faf2)', fg: 'var(--mv-primary,#4b9d35)' };
    const wrap = (chips) => html`<span style="display:flex; flex-wrap:wrap; gap:4px;">${chips}</span>`;
    // Alleen wát, niet waarom: de uitleg over opt-in en opt-out staat in de rol-uitleg (ⓘ bij
    // de kolom Rol). Een hover die een alinea wordt, leest niemand.
    const projectName = (p) => p.name || p.code || 'dit project';

    if (m.role === 'owner' || m.role === 'manager') {
      return tip('Eigenaren en beheerders hebben altijd toegang tot alle projecten.', word('alles', 'var(--mv-text-sub,#6c757d)'));
    }

    if (m.role === 'member') {
      const denied = m.deniedProjects || [];
      if (!denied.length) return tip('Toegang tot alle projecten.', word('alles', 'var(--mv-text-sub,#6c757d)'));
      return wrap(denied.map((p) => chip(p, RED, `Geen toegang tot ${projectName(p)}`)));
    }

    if (m.role === 'guest') {
      const allowed = m.guestProjects || [];
      if (!allowed.length) return tip('Nog geen toegang tot een project.', word('niets', 'var(--mv-text-muted,#a1a5b7)'));
      const LEVEL = { read: 'kijkt mee', write: 'werkt mee' };
      return wrap(allowed.map((p) => chip(p, GREEN, `Toegang tot ${projectName(p)} (${LEVEL[p.accessLevel] || 'kijkt mee'})`)));
    }

    return '';
  }

  get memberColumns() {
    return [
      {
        id: 'email', title: 'Lid',
        render: (m) => {
          const isMe = m.id === this.myUserId;
          const name = displayName(m);
          const hasName = name && name !== m.email; // displayName falls back to email
          return html`<div style="display:flex; align-items:center; gap:12px;">
            <span style="flex-shrink:0; width:34px; height:34px; border-radius:9px; background:var(--mv-primary); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:800;">${(name || m.email || '?').charAt(0).toUpperCase()}</span>
            <div style="display:flex; flex-direction:column; min-width:0;">
              <span style="font-weight:600; color:var(--mv-text);">${hasName ? name : m.email}${isMe ? html` <span style="font-size:0.75rem; color:var(--mv-text-sub,#6c757d); font-weight:500;">(jij)</span>` : ''}</span>
              ${hasName ? html`<span style="font-size:0.8rem; color:var(--mv-text-sub,#6c757d);">${m.email}</span>` : ''}
            </div>
          </div>`;
        },
      },
      {
        id: 'role', width: '170px',
        // ⓘ in de kop: dezelfde uitleg als bij het uitnodigen, hier in een popup.
        title: html`<span style="display:inline-flex; align-items:center; gap:5px;">Rol
          <button type="button" title="Wat mag elke rol?" @click=${(e) => { e.stopPropagation(); this._roleInfoModalOpen = true; }}
            style="border:none; background:none; cursor:pointer; color:var(--mv-primary); display:inline-flex; padding:0; line-height:1;">${unsafeSVG(lucideIcon(Info, 13))}</button>
        </span>`,
        // Een gast krijgt geen roldropdown: promoveren tot lid kan niet (de backend weigert
        // het, want dan ziet hij opeens alle projecten), dus een lijst met opties die alle
        // mislukken is erger dan een badge.
        // live(): de tabel sorteert op rol, dus na een wijziging schuiven rijen en krijgt
        // een hergebruikte <select> andere data. Zonder live() zet Lit .value niet opnieuw
        // als de waarde gelijk bleef, terwijl de gebruiker het veld net heeft omgezet.
        render: (m) => this.canManage(m) && m.role !== 'guest'
          ? html`<select .value=${live(m.role)} ?disabled=${this.busy}
              @click=${(e) => e.stopPropagation()}
              @change=${(e) => this.changeRole(m, e.target.value)}
              style=${`padding:6px 10px; border-radius:8px; border:1px solid var(--mv-border); background-color:var(--mv-surface,#fff); color:var(--mv-text); font-weight:600; cursor:pointer; ${CHEVRON_CSS}`}>
              ${this.assignableRoles.map((r) => html`<option value=${r} ?selected=${r === m.role}>${roleLabel(r)}</option>`)}
            </select>`
          : this.roleBadge(m.role),
      },
      {
        id: 'access', title: 'Toegang projecten',
        render: (m) => this.renderProjectAccess(m),
      },
      {
        id: 'last_activity', title: 'Laatste activiteit', width: '150px',
        // Wat iemand voor het laatst dééd in dit bedrijf; wie nog niets logde valt terug op
        // zijn laatste login, met een hover die het verschil benoemt.
        render: (m) => {
          const when = m.last_activity_at || m.last_login_at;
          if (!when) return html`<span style="font-size:0.8rem; color:var(--mv-text-muted,#a1a5b7);">—</span>`;
          const isLogin = !m.last_activity_at;
          return html`<span data-tip=${isLogin ? 'Nog geen activiteit vastgelegd; dit is de laatste keer dat dit lid inlogde.' : 'Laatste vastgelegde handeling in dit bedrijf.'}
            @mousemove=${tipMove} @mouseleave=${tipHide}
            style="font-size:0.82rem; color:${isLogin ? 'var(--mv-text-muted,#a1a5b7)' : 'var(--mv-text-sub,#6c757d)'}; white-space:nowrap; cursor:default;">${formatDateTimeSlash(when)}</span>`;
        },
      },
      {
        id: 'actions', title: '', width: '90px',
        // Helemaal rechts: ⋯ met de beheeracties, en daarnaast alleen nog het verwijderen. Dat
        // laatste staat los omdat het onomkeerbaar is en je het niet achter een menu wilt
        // moeten zoeken — maar ook niet per ongeluk wilt raken, dus het is het enige losse.
        render: (m) => html`<div style="display:flex; gap:6px; justify-content:flex-end;" @click=${(e) => e.stopPropagation()}>
          ${RANK[this.myRole] >= RANK.manager ? html`
            <div style="position:relative;">
              <button title="Meer acties" ?disabled=${this.busy} @click=${(e) => this._toggleMenu(m, e)}
                style="display:flex; align-items:center; justify-content:center; width:34px; height:34px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text-sub,#6c757d); cursor:pointer;">${unsafeSVG(lucideIcon(MoreHorizontal, 16))}</button>
              <!-- position:fixed, met de plek uit de knop berekend. De tabel knipt zijn inhoud af
                   (overflow hidden/auto), dus een absoluut menu valt binnen de tegel en maakt
                   hem tijdelijk breder -- dat was het verspringen. Zelfde oplossing als het
                   kolommenmenu van mv-table zelf. -->
              ${this._menuFor === m.id ? html`
                <div style="position:fixed; top:${this._menuPos?.top ?? 0}px; right:${this._menuPos?.right ?? 0}px; z-index:var(--mv-z-overlay,1100); min-width:230px; background:var(--mv-surface,#fff); border:1px solid var(--mv-border); border-radius:.7rem; box-shadow:0 10px 28px rgba(0,0,0,.14); padding:6px;">
                  <button style=${MENU_ITEM} @click=${() => { this._menuFor = null; this._activityUser = m; }}>
                    ${unsafeSVG(lucideIcon(Activity, 16))} Activiteit bekijken
                  </button>
                  <!-- Toegang beheren bij een lid (opt-out, vinkjes) én bij een gast (opt-in, met
                       niveau). Niet bij eigenaar/beheerder: daar valt niets te beperken. -->
                  ${(m.role === 'member' || m.role === 'guest') && this.canManage(m) ? html`
                    <button style=${MENU_ITEM} @click=${() => { this._menuFor = null; this.openAccess(m); }}>
                      ${unsafeSVG(lucideIcon(FolderCog, 16))} Toegang projecten beheren
                    </button>` : ''}
                  ${this.canTransfer(m) ? html`
                    <button style=${this.transferBlocked(m) ? `${MENU_ITEM} opacity:.45; cursor:not-allowed;` : MENU_ITEM}
                      ?disabled=${this.transferBlocked(m)}
                      title=${this.transferBlocked(m) ? 'Een gast kan geen eigenaar worden' : ''}
                      @click=${() => { this._menuFor = null; this.transfer(m); }}>
                      ${unsafeSVG(lucideIcon(Crown, 16))} Eigenaarschap overdragen
                    </button>` : ''}
                </div>` : ''}
            </div>` : ''}
          ${this.canManage(m) ? html`<button title="Verwijderen uit bedrijf" ?disabled=${this.busy} @click=${() => this.removeMember(m)}
            style="display:flex; align-items:center; justify-content:center; width:34px; height:34px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-danger,#dc3545); cursor:pointer;">${unsafeSVG(lucideIcon(UserMinus, 16))}</button>` : ''}
        </div>`,
      },
    ];
  }

  /**
   * Het ⋯-menu openen op de plek van de knop. Rechts uitgelijnd met de knop, net eronder;
   * de positie is in viewport-coördinaten omdat het menu position:fixed is.
   */
  _toggleMenu(m, e) {
    if (this._menuFor === m.id) { this._menuFor = null; return; }
    const r = e.currentTarget.getBoundingClientRect();
    this._menuPos = { top: r.bottom + 6, right: window.innerWidth - r.right };
    this._menuFor = m.id;
  }

  /**
   * Alle activiteit van één lid, over alle projecten heen. Dezelfde tegel als op een project,
   * in persoonsmodus: zelfde zinnen, zelfde uitklapbare wijzigingen, maar elke regel met zijn
   * project erbij — over projecten heen is 'wijzigde boring B03' anders niet te plaatsen.
   */
  renderActivityModal() {
    const m = this._activityUser;
    const name = displayName(m) || m.email;
    return html`
      <div @click=${(e) => e.target === e.currentTarget && (this._activityUser = null)}
        style="position:fixed; inset:0; background:rgba(0,0,0,0.4); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px;">
        <div style="background:var(--mv-surface,#fff); border-radius:16px; width:100%; max-width:760px; max-height:86vh; display:flex; flex-direction:column; box-shadow:0 12px 40px rgba(0,0,0,0.2);">
          <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px; padding:20px 24px 8px;">
            <div>
              <h2 style="margin:0; font-size:1.1rem; color:var(--mv-text);">Activiteit van ${name}</h2>
              <div style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d); margin-top:4px;">Alle projecten van dit bedrijf</div>
            </div>
            <button @click=${() => { this._activityUser = null; }} style="background:none; border:none; cursor:pointer; color:var(--mv-text-sub,#6c757d); display:flex; padding:4px;">
              ${unsafeSVG(lucideIcon(XIcon, 18))}
            </button>
          </div>
          <div style="flex:1; overflow-y:auto; padding:0 16px 16px;">
            <activity-tile .memberId=${m.id}></activity-tile>
          </div>
        </div>
      </div>`;
  }

  /**
   * De uitleg van de vier rollen — één bron, op twee plekken getoond: bij het uitnodigen (ⓘ
   * naast de rolkeuze) en in de tabel (ⓘ in de kolomkop Rol). Twee kopieën zouden uit elkaar
   * gaan lopen bij de eerstvolgende aanpassing.
   *
   * Per rol een kop, één inleidende zin en een opsomming in volledige zinnen — de tekst is
   * van de opdrachtgever (18-09-2026) en is bewust voluit: hier vergelijk je rollen, en dan
   * lees je van boven naar beneden. De begrippen opt-in en opt-out staan hier en niet in de
   * hovers op de projecttegels: daar wil je alleen wát, niet waarom.
   */
  renderRoleExplanation() {
    const T = 'color:var(--mv-text);';
    const role = (name, intro, items) => html`
      <div style="margin-top:14px;">
        <strong style="${T} font-size:0.88rem;">${name}</strong>
        <div style="margin-top:2px;">${intro}</div>
        <ul style="margin:4px 0 0; padding-left:1.05rem;">${items.map((li) => html`<li>${li}</li>`)}</ul>
      </div>`;
    return html`
      <div style="font-size:0.82rem; color:var(--mv-text-sub); line-height:1.5; font-weight:400; text-transform:none; letter-spacing:normal;">
        <div>Binnen een bedrijf zijn er vier rollen: <strong style=${T}>Eigenaar</strong>, <strong style=${T}>Beheerder</strong>, <strong style=${T}>Lid</strong> en <strong style=${T}>Gast</strong>. Elke rol heeft eigen rechten en verantwoordelijkheden.</div>

        ${role('Eigenaar', 'De eigenaar heeft de meeste rechten binnen het bedrijf.', [
          html`Er is <strong style=${T}>precies één eigenaar</strong> per bedrijf.`,
          html`De eigenaar heeft alle rechten van een beheerder.`,
          html`De eigenaar kan niet worden verwijderd en zijn rol kan niet door iemand anders worden gewijzigd.`,
          html`<strong style=${T}>Alleen de eigenaar</strong> kan het eigenaarschap overdragen aan een beheerder of lid.`,
          html`Na het overdragen van het eigenaarschap wordt de voormalige eigenaar automatisch beheerder.`,
        ])}

        ${role('Beheerder', 'Een beheerder beheert het bedrijf, de gebruikers en de projecten.', [
          html`Kan beheerders, leden en gasten uitnodigen en verwijderen.`,
          html`<strong style=${T}>Alleen een beheerder</strong> kan een andere gebruiker beheerder maken of als beheerder uitnodigen.`,
          html`Kan rollen van gebruikers wijzigen, behalve die van de eigenaar.`,
          html`Beheert sjablonen, recorder-instellingen en andere bedrijfsinstellingen.`,
          html`Heeft altijd toegang tot alle projecten. Deze toegang kan niet worden beperkt.`,
          html`Kan projecten aanmaken, aanpassen en <strong style=${T}>verwijderen</strong>.`,
        ])}

        ${role('Lid', 'Een lid werkt actief met projecten en opnames.', [
          html`Kan projecten en opnames gebruiken en digitaliseren.`,
          html`Kan projecten aanpassen, maar <strong style=${T}>niet verwijderen</strong>.`,
          html`Kan leden en gasten uitnodigen, maar geen beheerders.`,
          html`Kan geen rollen, sjablonen of bedrijfsinstellingen beheren.`,
          html`Heeft standaard toegang tot alle projecten.`,
          html`Een lid kan per project worden <strong style=${T}>uitgesloten</strong>. Dit werkt volgens het opt-outprincipe: toegang is standaard toegestaan, tenzij deze voor een specifiek project wordt ingetrokken.`,
          html`In de projecttabel wordt een uitgesloten project weergegeven met een rode tegel.`,
        ])}

        ${role('Gast', 'Een gast is een externe gebruiker, bijvoorbeeld een opdrachtgever of onderaannemer.', [
          html`Heeft standaard <strong style=${T}>geen</strong> toegang tot projecten.`,
          html`Toegang wordt per project afzonderlijk <strong style=${T}>toegekend</strong>. Dit werkt volgens het opt-inprincipe.`,
          html`In de projecttabel wordt een project waartoe de gast toegang heeft weergegeven met een groene tegel.`,
          html`Per project krijgt een gast bewerkrechten of leesrechten:
            <ul style="margin:2px 0 0; padding-left:1.05rem;">
              <li>Met <strong style=${T}>bewerkrechten</strong> kan de gast binnen dat project hetzelfde doen als een lid.</li>
              <li>Met <strong style=${T}>leesrechten</strong> kan de gast het project alleen bekijken en niets aanpassen. Dit is bijvoorbeeld geschikt voor een opdrachtgever die alleen wil meekijken.</li>
            </ul>`,
          html`Een gast kan geen gebruikers uitnodigen.`,
          html`Een gast kan zichzelf wel uit een project verwijderen.`,
        ])}
      </div>`;
  }

  /** Popup met de rol-uitleg, vanuit het ⓘ in de kolomkop Rol. */
  renderRoleInfoModal() {
    return html`
      <div @click=${(e) => e.target === e.currentTarget && (this._roleInfoModalOpen = false)}
        style="position:fixed; inset:0; background:rgba(0,0,0,0.4); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px;">
        <div style="background:var(--mv-surface,#fff); border-radius:16px; width:100%; max-width:520px; max-height:86vh; overflow-y:auto; box-shadow:0 12px 40px rgba(0,0,0,0.2);">
          <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px; padding:22px 24px 8px;">
            <h2 style="margin:0; font-size:1.1rem; color:var(--mv-text);">Rollen en rechten</h2>
            <button @click=${() => { this._roleInfoModalOpen = false; }} style="background:none; border:none; cursor:pointer; color:var(--mv-text-sub,#6c757d); display:flex; padding:4px;">
              ${unsafeSVG(lucideIcon(XIcon, 18))}
            </button>
          </div>
          <div style="padding:6px 24px 22px;">${this.renderRoleExplanation()}</div>
        </div>
      </div>`;
  }

  /**
   * Werkbalk boven de tabel: zoeken op naam of e-mail, filteren op rol, en rechts de knop om
   * iemand uit te nodigen. De knop opent een popup — het formulier hoort niet permanent
   * bovenaan te staan als je meestal komt om te kijken wie er in het team zit.
   */
  renderToolbar() {
    // Witte achtergrond, niet --mv-bg: deze velden staan direct op de pagina, en die is zelf
    // al --mv-bg. In een formulier op een witte tegel is grijs juist goed; hier vielen ze weg.
    const field = 'padding:0.55rem 0.8rem; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); font-size:0.88rem; color:var(--mv-text); background:var(--mv-surface,#fff); outline:none; font-family:var(--mv-font);';
    return html`
      <div style="display:flex; gap:10px; align-items:center; margin-bottom:14px; flex-wrap:wrap;">
        <div style="position:relative; flex:1 1 220px; max-width:360px;">
          <span style="position:absolute; left:10px; top:50%; transform:translateY(-50%); color:var(--mv-text-muted,#a1a5b7); display:flex;">${unsafeSVG(lucideIcon(Search, 15))}</span>
          <input type="search" placeholder="Zoeken op naam of e-mail…" .value=${this._search}
            @input=${(e) => { this._search = e.target.value; }}
            style="${field} width:100%; box-sizing:border-box; padding-left:32px;" />
        </div>
        <select .value=${this._roleFilter} @change=${(e) => { this._roleFilter = e.target.value; }}
          style=${`${field} cursor:pointer; width:160px; ${CHEVRON_CSS}`}>
          <option value="" ?selected=${!this._roleFilter}>Alle rollen</option>
          ${['owner', 'manager', 'member', 'guest'].map((r) => html`<option value=${r} ?selected=${r === this._roleFilter}>${roleLabel(r)}</option>`)}
        </select>
        <!-- margin-left:auto en geen flex:1-opvuller: een opvuller houdt de knop op dezelfde
             regel en duwt hem op een smal scherm uit beeld, terwijl auto-marge de knop
             gewoon naar rechts zet en hem netjes laat afbreken. -->
        ${this.canInvite ? html`
          <button @click=${() => { this._inviteOpen = true; this.inviteError = null; this.inviteResult = null; }}
            style="display:flex; align-items:center; gap:8px; margin-left:auto; padding:0.6rem 1rem; border-radius:var(--mv-radius-sm); border:none; background:var(--mv-primary); color:#fff; font-weight:700; font-size:0.88rem; cursor:pointer; font-family:var(--mv-font); white-space:nowrap;">
            ${unsafeSVG(lucideIcon(UserPlus, 16))} Uitnodigen
          </button>` : ''}
      </div>`;
  }

  /** Leden na zoeken en rolfilter. Zoekt in naam én e-mail: je weet niet altijd hoe iemand heet. */
  get filteredMembers() {
    const q = (this._search || '').trim().toLowerCase();
    return (this.members || []).filter((m) => {
      if (this._roleFilter && m.role !== this._roleFilter) return false;
      if (!q) return true;
      return `${displayName(m) || ''} ${m.email || ''}`.toLowerCase().includes(q);
    });
  }

  /** Uitnodigingen na zoeken en rolfilter; zoekt in e-mail en de opgegeven naam. */
  get filteredInvitations() {
    const q = (this._search || '').trim().toLowerCase();
    return (this.invitations || []).filter((inv) => {
      if (this._roleFilter && inv.role !== this._roleFilter) return false;
      if (!q) return true;
      return `${inv.first_name || ''} ${inv.last_name || ''} ${inv.email || ''}`.toLowerCase().includes(q);
    });
  }

  /**
   * Het uitnodigformulier, in een popup. Velden onder elkaar: in een venster van 480px leest
   * dat beter dan vier velden naast elkaar, en de gastprojectkeuze past er dan gewoon onder.
   */
  renderInviteModal() {
    const labelStyle = 'display:block; font-size:0.8rem; font-weight:600; color:var(--mv-text-sub); margin-bottom:0.4rem; text-transform:uppercase; letter-spacing:0.04em;';
    const fieldStyle = 'width:100%; padding:0.65rem 0.9rem; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); font-size:0.9rem; color:var(--mv-text); background:var(--mv-bg); outline:none; box-sizing:border-box; font-family:var(--mv-font); transition:border 0.15s;';
    return html`
      <div @click=${(e) => e.target === e.currentTarget && (this._inviteOpen = false)}
        style="position:fixed; inset:0; background:rgba(0,0,0,0.4); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px;">
        <div style="background:var(--mv-surface,#fff); border-radius:16px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; box-shadow:0 12px 40px rgba(0,0,0,0.2);">
          <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px; padding:22px 24px 6px;">
            <div>
              <h2 style="margin:0; font-size:1.1rem; color:var(--mv-text);">Iemand uitnodigen</h2>
              <div style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d); margin-top:4px;">Voor ${auth.getActiveCompany()?.name || 'dit bedrijf'}</div>
            </div>
            <button @click=${() => { this._inviteOpen = false; }} style="background:none; border:none; cursor:pointer; color:var(--mv-text-sub,#6c757d); display:flex; padding:4px;">
              ${unsafeSVG(lucideIcon(XIcon, 18))}
            </button>
          </div>
      <form @submit=${this.submitInvite}
        style="display:flex; gap:1rem; flex-wrap:wrap; align-items:flex-end; padding:12px 24px 22px;">
        <div style="flex:1; min-width:150px;">
          <label style=${labelStyle}>Voornaam</label>
          <input type="text" required placeholder="Voornaam"
            .value=${this.inviteFirstName} @input=${(e) => { this.inviteFirstName = e.target.value; }}
            style=${fieldStyle} />
        </div>
        <div style="flex:1; min-width:150px;">
          <label style=${labelStyle}>Achternaam</label>
          <input type="text" required placeholder="Achternaam"
            .value=${this.inviteLastName} @input=${(e) => { this.inviteLastName = e.target.value; }}
            style=${fieldStyle} />
        </div>
        <div style="flex-basis:100%;">
          <label style=${labelStyle}>E-mailadres</label>
          <input type="email" required placeholder="collega@bedrijf.nl"
            .value=${this.inviteEmail} @input=${(e) => { this.inviteEmail = e.target.value; }}
            style=${fieldStyle} />
        </div>
        <div style="flex-basis:100%; position:relative;">
          <label style="${labelStyle} display:flex; align-items:center; gap:6px;">
            <span>Rol</span>
            <button type="button" @click=${() => { this._roleInfoOpen = !this._roleInfoOpen; }}
              title="Verschil tussen beheerder en lid"
              style="border:none; background:none; cursor:pointer; color:var(--mv-primary); font-size:0.95rem; line-height:1; padding:0;">ⓘ</button>
          </label>
          <select required .value=${this.inviteRole} @change=${(e) => this._onInviteRoleChange(e.target.value)}
            style=${`${fieldStyle} cursor:pointer; ${CHEVRON_CSS}`}>
            <option value="" disabled ?selected=${!this.inviteRole}>Kies een rol…</option>
            ${this.roleOptions.map((o) => html`
              <option value=${o.value} ?disabled=${!o.allowed} ?selected=${o.value === this.inviteRole}
                title=${o.allowed ? '' : o.why}>${o.label}${o.allowed ? '' : ' (niet beschikbaar)'}</option>`)}
          </select>
          ${this._roleInfoOpen ? html`
            <!-- In de flow en niet zwevend: de popup knipt af (overflow-y:auto), dus een
                 absoluut blok zou half verdwijnen. Zo groeit het venster gewoon mee. -->
            <div style="margin-top:8px; background:var(--mv-surface-alt,#f8f9fa); border:1px solid var(--mv-border); border-radius:10px; padding:14px 16px; text-transform:none; letter-spacing:normal;">
              <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
                <strong style="font-size:0.9rem; color:var(--mv-text);">Rollen en rechten</strong>
                <button type="button" @click=${() => { this._roleInfoOpen = false; }}
                  style="border:none; background:none; cursor:pointer; color:var(--mv-text-muted); font-size:1rem; line-height:1; padding:0;">✕</button>
              </div>
              ${this.renderRoleExplanation()}
            </div>` : ''}
        </div>

        <!-- Alleen bij 'Gast': het project en het niveau. Klapt uit op de volgende regel, want
             een gast hoort bij één project en zonder dat heeft hij nergens toegang toe. -->
        ${this.inviteRole === 'guest' ? html`
          <div style="flex-basis:100%; display:flex; gap:12px; flex-wrap:wrap; align-items:flex-end;">
            <div class="invite-project" style="flex:1; min-width:240px;">
              <label style=${labelStyle}>Project</label>
              <!-- Doorzoekbaar: een bedrijf heeft al snel tientallen projecten, en je weet de
                   code of een deel van de naam. Dezelfde component als elders in de app.
                   Het knopvlak zit in de schaduw-DOM en erft fieldStyle dus niet; via ::part
                   krijgt het hetzelfde grijze vlak en dezelfde maten als de velden erboven. -->
              <style>
                .invite-project searchable-select::part(trigger) {
                  padding:0.65rem 0.9rem; border-radius:var(--mv-radius-sm); font-size:0.9rem;
                  background:var(--mv-bg); font-family:var(--mv-font);
                }
              </style>
              <searchable-select
                placeholder=${this._projects === null ? 'Projecten laden…' : 'Zoek een project…'}
                .options=${(this._projects || []).map((p) => ({
                  value: p.id,
                  label: `${p.project_code ? `${p.project_code} — ` : ''}${p.project_name || p.name || '(naamloos)'}`,
                }))}
                .value=${this.inviteProjectId}
                @change=${(e) => { this.inviteProjectId = e.detail.value; }}>
              </searchable-select>
            </div>
            <div style="min-width:220px;">
              <label style=${labelStyle}>Toegang</label>
              <div style="display:flex; gap:6px;">
                ${[['read', 'Kijkt mee'], ['write', 'Werkt mee']].map(([v, label]) => html`
                  <button type="button" @click=${() => { this.inviteAccessLevel = v; }}
                    style="flex:1; padding:0.6rem 0.8rem; border-radius:var(--mv-radius-sm); cursor:pointer; font:inherit; font-size:0.88rem; font-weight:600;
                           border:2px solid ${this.inviteAccessLevel === v ? 'var(--mv-primary)' : 'var(--mv-border)'};
                           background:${this.inviteAccessLevel === v ? 'var(--mv-primary-light,#f4faf2)' : 'var(--mv-surface,#fff)'}; color:var(--mv-text);">${label}</button>`)}
              </div>
            </div>
          </div>` : ''}

        ${this.inviteError ? html`<div style="flex-basis:100%; padding:10px 14px; border-radius:8px; background:rgba(220,53,69,0.1); color:var(--mv-danger,#dc3545); font-size:0.88rem;">${this.inviteError}</div>` : ''}

        <div style="flex-basis:100%; display:flex; justify-content:flex-end; gap:8px; margin-top:4px;">
          <button type="button" @click=${() => { this._inviteOpen = false; }}
            style="padding:0.65rem 1rem; border-radius:var(--mv-radius-sm); border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text); font-weight:600; font-size:0.9rem; cursor:pointer; font-family:var(--mv-font);">Annuleren</button>
          <button type="submit" ?disabled=${this.busy}
            style="display:flex; align-items:center; gap:8px; padding:0.65rem 1.1rem; border-radius:var(--mv-radius-sm); border:none; background:var(--mv-primary); color:#fff; font-weight:700; font-size:0.9rem; cursor:pointer; font-family:var(--mv-font);">
            ${unsafeSVG(lucideIcon(Mail, 16))} ${this.busy ? 'Bezig…' : 'Uitnodigen'}
          </button>
        </div>
      </form>
        </div>
      </div>`;
  }

  /** Bevestiging na een uitnodiging, onder de werkbalk: naast de nieuwe rij, met de kopieerlink. */
  renderInviteResult() {
    return html`
      ${this.inviteResult?.added ? html`
        <div style="padding:10px 14px; border-radius:8px; background:rgba(34,197,94,0.12); color:#15803d; margin-bottom:16px;">
          ${this.inviteResult.email} is toegevoegd aan dit bedrijf${this.inviteResult.emailed ? ' en per e-mail op de hoogte gebracht' : ''}.
        </div>
      ` : ''}
      ${this.inviteResult?.invited ? html`
        <div style="padding:12px 14px; border-radius:8px; background:rgba(59,130,246,0.1); color:var(--mv-text); margin-bottom:16px;">
          <div style="font-weight:600; margin-bottom:6px;">${this.inviteResult.emailed ? `Uitnodiging verstuurd naar ${this.inviteResult.email}` : `Uitnodiging aangemaakt voor ${this.inviteResult.email}`}</div>
          <div style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d); margin-bottom:8px;">${this.inviteResult.emailed ? 'We hebben een uitnodigingsmail gestuurd. Je kunt de link desgewenst ook zelf delen:' : 'E-mail kon niet worden verstuurd — deel de uitnodigingslink zelf:'}</div>
          <div style="display:flex; gap:8px;">
            <input readonly .value=${this.acceptUrl(this.inviteResult.token)}
              style="flex:1; padding:8px 10px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text); font-size:0.82rem;" />
            <button @click=${() => this.copy(this.acceptUrl(this.inviteResult.token), this.inviteResult.token)}
              style="display:flex; align-items:center; gap:6px; padding:8px 12px; border-radius:8px; border:1px solid var(--mv-primary); background:none; color:var(--mv-primary); font-weight:600; cursor:pointer; white-space:nowrap;">
              ${unsafeSVG(lucideIcon(this.copiedToken === this.inviteResult.token ? Check : Copy, 15))}
              ${this.copiedToken === this.inviteResult.token ? 'Gekopieerd' : 'Kopieer link'}
            </button>
          </div>
        </div>
      ` : ''}
    `;
  }

  get invitationColumns() {
    return [
      {
        id: 'email', title: 'E-mailadres',
        render: (inv) => html`<div style="display:flex; align-items:center; gap:12px;">
          <span style="flex-shrink:0; width:34px; height:34px; border-radius:9px; background:var(--mv-bg,#e9ecef); color:var(--mv-text-sub,#6c757d); display:flex; align-items:center; justify-content:center;">${unsafeSVG(lucideIcon(Mail, 18))}</span>
          <span style="font-weight:600; color:var(--mv-text);">${inv.email}</span>
        </div>`,
      },
      { id: 'role', title: 'Rol', width: '120px', render: (inv) => this.roleBadge(inv.role) },
      {
        id: 'actions', title: '', width: '210px',
        render: (inv) => html`<div style="display:flex; gap:8px; justify-content:flex-end;" @click=${(e) => e.stopPropagation()}>
          <button @click=${() => this.copyInviteLink(inv)}
            style="display:flex; align-items:center; gap:6px; padding:8px 12px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text); font-weight:600; cursor:pointer; white-space:nowrap;">
            ${unsafeSVG(lucideIcon(this.copiedToken === inv.id ? Check : Copy, 15))}
            ${this.copiedToken === inv.id ? 'Gekopieerd' : 'Kopieer link'}
          </button>
          <button @click=${() => this.revoke(inv)} ?disabled=${this.busy} title="Intrekken"
            style="display:flex; align-items:center; justify-content:center; width:36px; height:36px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-danger,#dc3545); cursor:pointer;">
            ${unsafeSVG(lucideIcon(Trash2, 16))}
          </button>
        </div>`,
      },
    ];
  }

  /**
   * Tegel onder de ledenlijst: mogen systeembeheerders van GeoVoice meekijken?
   *
   * Staat hier omdat het over toegang tot jouw organisatie gaat, net als de ledenlijst erboven.
   * De regel eronder zegt alles wat je moet weten (voor support, lezen, niet wijzigen); een
   * ⓘ met een langere versie van diezelfde zin stond er eerst bij en is weggehaald.
   */
  renderSysadminAccess() {
    const mayEdit = RANK[this.myRole] >= RANK.manager;
    return html`
      <div style="margin-top:22px; padding:16px; border:1px solid var(--mv-border); border-radius:12px; background:var(--mv-surface,#fff);">
        <div style="display:flex; justify-content:space-between; align-items:center; gap:16px;">
          <div>
            <div style="font-weight:600; color:var(--mv-text);">Systeembeheerders mogen meekijken</div>
            <div style="margin-top:2px; font-size:0.85rem; color:var(--mv-text-sub,#6c757d); max-width:460px;">
              Voor support. Ze kunnen je gegevens inzien, maar niets wijzigen.
              ${mayEdit ? '' : ' Alleen een eigenaar of beheerder kan dit omzetten.'}
            </div>
          </div>
          ${this._toggle(!!this.sysadminAccess, mayEdit && !this.busy, (v) => this.saveSysadminAccess(v))}
        </div>
      </div>`;
  }

  /** Een schakelaar. Inline gestyled, net als de rest van deze pagina. */
  _toggle(checked, enabled, onChange) {
    return html`
      <span role="switch" aria-checked=${checked ? 'true' : 'false'}
        @click=${() => { if (enabled) onChange(!checked); }}
        style="flex:none; width:44px; height:24px; border-radius:999px; position:relative;
               cursor:${enabled ? 'pointer' : 'default'}; opacity:${enabled ? '1' : '0.6'};
               background:${checked ? 'var(--mv-primary)' : 'var(--mv-border)'}; transition:background .15s;">
        <span style="position:absolute; top:2px; left:${checked ? '22px' : '2px'}; width:20px; height:20px;
                     border-radius:50%; background:#fff; transition:left .15s;
                     box-shadow:0 1px 3px rgba(0,0,0,.25);"></span>
      </span>`;
  }

  tabButton(id, label, badge) {
    const active = this.tab === id;
    // Switching tabs clears the transient invite result/error banner.
    return html`<button @click=${() => { this.tab = id; this.inviteResult = null; this.inviteError = null; }}
      style="display:inline-flex; align-items:center; gap:8px; padding:10px 4px; border:none; background:none; cursor:pointer; font-weight:500; font-size:0.95rem; color:${active ? 'var(--mv-primary)' : 'var(--mv-text-sub,#6c757d)'}; border-bottom:2px solid ${active ? 'var(--mv-primary)' : 'transparent'};">
      ${label}${badge ? html`<span style="display:inline-flex; align-items:center; justify-content:center; min-width:20px; height:20px; padding:0 6px; border-radius:999px; background:var(--mv-primary); color:#fff; font-size:0.72rem; font-weight:700;">${badge}</span>` : ''}
    </button>`;
  }

  render() {
    return html`
      <!-- Breed genoeg voor vijf kolommen op één regel. Op 760px brak de naamkolom af zodra
           'Toegang projecten' en 'Laatste activiteit' erbij kwamen. -->
      <div style="max-width:1180px;">
        <div style="margin-bottom:6px;">
          <h1 style="margin:0; font-size:1.5rem; font-weight:700; color:var(--mv-text);">Team</h1>
          <p style="margin:4px 0 0; font-size:0.9rem; color:var(--mv-text-sub,#6c757d);">
            Nodig mensen uit voor <b style="color:var(--mv-text);">${auth.getActiveCompany()?.name || 'dit bedrijf'}</b> zodat ze toegang krijgen tot projecten.
          </p>
        </div>

        ${this.error ? html`<div style="padding:10px 14px; border-radius:8px; background:rgba(220,53,69,0.1); color:var(--mv-danger,#dc3545); margin:12px 0;">${this.error}</div>` : ''}

        <div style="display:flex; gap:20px; border-bottom:1px solid var(--mv-border); margin:16px 0 16px;">
          ${this.tabButton('members', 'Leden', null)}
          ${this.canSeeInvitations ? this.tabButton('invitations', 'Uitnodigingen', this.invitations.length) : ''}
        </div>

        ${this.renderToolbar()}
        ${this.renderInviteResult()}

        ${this.tab === 'members' ? html`
          <mv-table
            .columns=${this.memberColumns}
            .data=${this.filteredMembers}
            emptyText=${this.loading ? 'Leden laden…' : (this._search || this._roleFilter) ? 'Geen leden die hieraan voldoen.' : 'Geen leden.'}
          ></mv-table>
          ${this.sysadminAccessLoaded ? this.renderSysadminAccess() : ''}
        ` : html`
          <mv-table
            .columns=${this.invitationColumns}
            .data=${this.filteredInvitations}
            emptyText=${this.loading ? 'Laden…' : 'Geen openstaande uitnodigingen.'}
          ></mv-table>
        `}
      </div>

      ${this._inviteOpen ? this.renderInviteModal() : ''}
      ${this._roleInfoModalOpen ? this.renderRoleInfoModal() : ''}
      ${this.accessUser ? this.renderAccessModal() : ''}
      ${this._activityUser ? this.renderActivityModal() : ''}
    `;
  }

  renderAccessModal() {
    const isGuest = this.accessUser?.role === 'guest';
    const denied = this.accessProjects.filter(p => !p.hasAccess).length;
    const granted = this.accessProjects.filter(p => p.hasAccess).length;
    // Zoeken op naam of code. De teller onderin blijft over álle projecten gaan: wat je
    // hebt aangezet buiten het zoekresultaat telt gewoon mee, en wordt ook opgeslagen.
    const q = (this._accessQuery || '').trim().toLowerCase();
    const visible = q
      ? this.accessProjects.filter(p => (p.name || '').toLowerCase().includes(q) || (p.code || '').toLowerCase().includes(q))
      : this.accessProjects;
    return html`
      <div @click=${(e) => e.target === e.currentTarget && this.closeAccess()}
        style="position:fixed; inset:0; background:rgba(0,0,0,0.4); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px;">
        <div style="background:var(--mv-surface,#fff); border-radius:16px; width:100%; max-width:520px; max-height:80vh; display:flex; flex-direction:column; box-shadow:0 12px 40px rgba(0,0,0,0.2);">
          <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px; padding:24px 24px 12px;">
            <div>
              <h2 style="margin:0; font-size:1.1rem; color:var(--mv-text);">Projecttoegang</h2>
              <div style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d); margin-top:4px;">${this.accessUser.email}</div>
            </div>
            <button @click=${this.closeAccess} style="background:none; border:none; font-size:1.4rem; line-height:1; color:var(--mv-text-sub,#6c757d); cursor:pointer;">&times;</button>
          </div>
          <div style="padding:0 24px 12px; font-size:0.85rem; color:var(--mv-text-sub,#6c757d);">
            ${isGuest
              ? 'Een gast ziet alleen de projecten die je hier toekent. Kies per project of de gast meekijkt of meewerkt.'
              : 'Dit lid heeft standaard toegang tot alle projecten. Zet een project uit om het voor dit lid te verbergen.'}
          </div>
          <div style="position:relative; padding:0 24px 10px;">
            <span style="position:absolute; left:34px; top:50%; transform:translateY(-50%) translateY(-5px); color:var(--mv-text-muted,#a1a5b7); display:flex;">${unsafeSVG(lucideIcon(Search, 15))}</span>
            <input type="search" placeholder="Zoek een project…" .value=${this._accessQuery || ''}
              @input=${(e) => { this._accessQuery = e.target.value; }}
              style="width:100%; box-sizing:border-box; padding:0.55rem 0.8rem 0.55rem 32px; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); font-size:0.88rem; color:var(--mv-text); background:var(--mv-surface,#fff); outline:none; font-family:var(--mv-font);" />
          </div>

          <div style="flex:1; overflow-y:auto; padding:0 24px;">
            ${this.accessLoading ? html`<div style="padding:24px; text-align:center; color:var(--mv-text-sub,#6c757d);">Laden…</div>`
              : this.accessProjects.length === 0 ? html`<div style="padding:24px; text-align:center; color:var(--mv-text-sub,#6c757d);">Geen projecten in dit bedrijf.</div>`
              : visible.length === 0 ? html`<div style="padding:24px; text-align:center; color:var(--mv-text-sub,#6c757d);">Geen projecten die hieraan voldoen.</div>`
              : html`<div style="display:flex; flex-direction:column; gap:6px;">
                  ${visible.map(p => isGuest ? html`
                    <!-- Gast: drieweg-keuze in plaats van een vinkje. Geen / kijkt mee / werkt
                         mee, als knoppenrij: drie opties lezen sneller dan een dropdown. -->
                    <div style="display:flex; align-items:center; gap:12px; padding:10px 12px; border:1px solid var(--mv-border); border-radius:8px;">
                      <span style="flex:1; min-width:0;">
                        <span style="font-weight:600; color:var(--mv-text); display:block; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${p.name || '(naamloos project)'}</span>
                        ${p.code ? html`<span style="font-size:0.78rem; color:var(--mv-text-sub,#6c757d);">${p.code}</span>` : ''}
                      </span>
                      <div style="display:flex; gap:4px; flex:none;">
                        ${[[null, 'Geen'], ['read', 'Kijkt mee'], ['write', 'Werkt mee']].map(([v, label]) => {
                          const on = (p.accessLevel || null) === v;
                          // 'Geen' kleurt rood als het aanstaat: groen zou zeggen dat er iets
                          // is toegekend, terwijl juist het tegendeel geldt.
                          const tone = v === null ? 'var(--mv-danger,#f1416c)' : 'var(--mv-primary)';
                          return html`<button type="button" @click=${() => this.setGuestLevel(p.id, v)}
                            style="padding:5px 10px; border-radius:6px; font:inherit; font-size:0.78rem; font-weight:600; cursor:pointer;
                                   border:1px solid ${on ? tone : 'var(--mv-border)'};
                                   background:${on ? tone : 'var(--mv-surface,#fff)'}; color:${on ? '#fff' : 'var(--mv-text-sub,#6c757d)'};">${label}</button>`;
                        })}
                      </div>
                    </div>
                  ` : html`
                    <label style="display:flex; align-items:center; gap:12px; padding:10px 12px; border:1px solid var(--mv-border); border-radius:8px; cursor:pointer;">
                      <input type="checkbox" .checked=${p.hasAccess} @change=${() => this.toggleProjectAccess(p.id)} style="width:18px; height:18px; cursor:pointer; accent-color:var(--mv-primary);" />
                      <span style="flex:1; min-width:0;">
                        <span style="font-weight:600; color:var(--mv-text); display:block; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${p.name || '(naamloos project)'}</span>
                        ${p.code ? html`<span style="font-size:0.78rem; color:var(--mv-text-sub,#6c757d);">${p.code}</span>` : ''}
                      </span>
                      <span style="font-size:0.78rem; font-weight:600; color:${p.hasAccess ? 'var(--mv-primary)' : 'var(--mv-text-sub,#6c757d)'};">${p.hasAccess ? 'Toegang' : 'Verborgen'}</span>
                    </label>
                  `)}
                </div>`}
          </div>

          <div style="display:flex; justify-content:space-between; align-items:center; gap:12px; padding:16px 24px; border-top:1px solid var(--mv-border); margin-top:12px;">
            <span style="font-size:0.82rem; color:var(--mv-text-sub,#6c757d);">${isGuest
              ? (granted === 0 ? 'Nog geen projecten' : `Toegang tot ${granted} project${granted === 1 ? '' : 'en'}`)
              : (denied === 0 ? 'Toegang tot alle projecten' : `${denied} project${denied === 1 ? '' : 'en'} verborgen`)}</span>
            <div style="display:flex; gap:8px;">
              <button @click=${this.closeAccess} style="padding:9px 16px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text); font-weight:600; cursor:pointer;">Annuleren</button>
              <button @click=${this.saveAccess} ?disabled=${this.accessBusy || this.accessLoading} style="padding:9px 16px; border-radius:8px; border:none; background:var(--mv-primary); color:#fff; font-weight:700; cursor:pointer;">${this.accessBusy ? 'Opslaan…' : 'Opslaan'}</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('team-page', TeamPage);
